/**
 * Core CRM entity types: contact, company, deal, meeting, note, meeting notes.
 * Split out of entityTypeRegistry.ts — see that file for the merge point.
 */

import { parseFullName } from 'parse-full-name';

import { type DealQuantityValue, getPropertyValue } from '@/types/crm';
import { formatCurrency } from '@/utils/currency';

import { EntityHoverCompanyPreview } from '../../components/badges/EntityHoverCompanyPreview';
import { EntityHoverContactPreview } from '../../components/badges/EntityHoverContactPreview';
import type { CrmObject } from '../../core/types';
import { getDisplayName } from '../../utils/getDisplayName';
import { extractDomain, isEmail } from '../../utils/parseUtils';
import { CLOSED_RISK_STATUSES } from '../../utils/riskStatus';
import { NoteRecordOverview } from '../notes/NoteRecordOverview';
import { AccessBadgeSubHeader } from '../sections/access-badge-sub-header';
import { BookingAnswersRows } from '../sections/booking-answers-rows';
import { CompanyPeopleSection } from '../sections/company-people-section';
import { CompanySubHeader } from '../sections/company-sub-header';
import { ContactSubHeader } from '../sections/contact-sub-header';
import { DealPeopleSection } from '../sections/deal-people-section';
import { DealSubHeader } from '../sections/deal-sub-header';
import { MeetingNotesDetailSection } from '../sections/meeting-notes-detail-section';
import { MeetingSeriesSection } from '../sections/meeting-series-section';
import { MeetingSubHeader } from '../sections/meeting-sub-header';
import { MeetingSummary } from '../sections/meeting-summary';
import { MeetingTypeSpecific } from '../sections/meeting-type-specific';
import { TaskRecordOverview } from '../task/TaskRecordOverview';
import type { EntityTypeConfig } from './entityTypeRegistry';
import { gmailAction, linkedinAction, websiteAction } from './headerActions';
import type {
  RelatedEntitySectionConfig,
  TypeSpecificSectionEntry,
} from './relatedSections';

/**
 * "Upcoming meetings": future meetings associated with the slideout entity.
 * Resolved server-side via `associated_with_entity_id` — for a contact this is
 * `ahoy_contact_to_meeting`, for a deal the materialized `ahoy_deal_to_meeting`
 * — filtered to `ahoy_start_time >= now`. (Replaces the legacy client-side
 * email-match against `ahoy_invitations`.)
 */
const UPCOMING_MEETINGS_SECTION: RelatedEntitySectionConfig = {
  key: 'upcoming-meetings',
  title: 'Upcoming meetings',
  entityType: 'ahoy_meeting',
  source: { kind: 'association' },
  filters: [
    {
      kind: 'property-compare',
      property: 'ahoy_start_time',
      operator: 'gte',
      value: 'now',
    },
    // Exclude cancelled meetings. Beyond normal cancellations, the Outlook
    // recurring-series id migration flips the orphaned pre-parity entity to
    // status=cancelled while it keeps a (possibly future) start_time — without
    // this it would render as a stray cancelled row next to the live occurrences.
    {
      kind: 'property-compare',
      property: 'ahoy_status',
      operator: 'ne',
      value: 'cancelled',
    },
  ],
  sort: { property: 'ahoy_start_time', order: 'asc' },
  limit: 5,
  properties: [
    'ahoy_title',
    'ahoy_start_time',
    'ahoy_end_time',
    'ahoy_meeting_link',
    'ahoy_status',
    'ahoy_color',
    'ahoy_origin',
  ],
  includeAssociations: {
    ahoy_meeting_to_company: { properties: ['ahoy_name'] },
  },
  card: 'meetingCard',
  emptyState: 'No upcoming meetings',
  errorMessage: "We couldn't load your upcoming meetings.",
  itemNounPlural: 'meetings',
  itemNounSingular: 'meeting',
};

/**
 * "Meeting notes": historical meeting-notes entities associated with the
 * slideout entity. The two-hop relationship (entity → meetings → notes) is
 * pre-materialized by the CRM as a single association
 * (`ahoy_contact_to_meeting_notes` for contacts, `ahoy_case_to_meeting_notes`
 * for deals), so `associated_with_entity_id` resolves it in one hop. The same
 * config serves both contact and deal — the slideout entity id selects the
 * right association.
 */
const MEETING_NOTES_SECTION: RelatedEntitySectionConfig = {
  key: 'meeting-notes',
  title: 'Meeting notes',
  entityType: 'ahoy_meeting_notes',
  source: { kind: 'association' },
  sort: { property: 'ahoy_created_at', order: 'desc' },
  limit: 5,
  properties: [
    'ahoy_title',
    'ahoy_summary',
    'ahoy_action_items',
    'ahoy_key_topics',
    'ahoy_created_at',
  ],
  // Hydrate the originating meeting so the card (rendered outside the meeting
  // slideout) can show the meeting's name + time and link back to it.
  includeAssociations: {
    ahoy_meeting_notes_to_meeting: {
      properties: ['ahoy_title', 'ahoy_start_time'],
    },
  },
  card: 'meetingNotesCard',
  emptyState: 'No meeting notes yet',
  errorMessage: "We couldn't load meeting notes.",
  itemNounPlural: 'notes',
  itemNounSingular: 'note',
};

/**
 * "Risks": open risks associated with the slideout entity, resolved via
 * `associated_with_entity_id`. Excludes resolved/dismissed risks so the
 * section only surfaces risks that still need attention.
 */
const RISKS_SECTION: RelatedEntitySectionConfig = {
  key: 'risks',
  title: 'Risks',
  entityType: 'ahoy_risk',
  source: { kind: 'association' },
  // One `ne` filter per closed status, derived from the shared list so this
  // stays in step with the client-side predicate in deal-status-section.
  filters: CLOSED_RISK_STATUSES.map(status => ({
    kind: 'property-compare' as const,
    property: 'ahoy_status',
    operator: 'ne' as const,
    value: status,
  })),
  sort: { property: 'ahoy_created_at', order: 'desc' },
  limit: 5,
  properties: [
    'ahoy_title',
    'ahoy_description',
    'ahoy_severity',
    'ahoy_status',
    'ahoy_risk_category',
    'ahoy_created_at',
  ],
  card: 'riskCard',
  hideWhenEmpty: true,
  emptyState: 'No risks',
  errorMessage: "We couldn't load risks.",
  itemNounPlural: 'risks',
  itemNounSingular: 'risk',
};

/** Contact slideout: risks + upcoming meetings + notes. */
const CONTACT_TYPE_SPECIFIC_SECTIONS: TypeSpecificSectionEntry[] = [
  { kind: 'section', config: RISKS_SECTION },
  { kind: 'section', config: UPCOMING_MEETINGS_SECTION },
  { kind: 'section', config: MEETING_NOTES_SECTION },
];

/** Core CRM entity types: contact, company, deal, meeting, note, meeting notes. */
export const CORE_ENTITY_TYPES: Record<string, EntityTypeConfig> = {
  ahoy_contact: {
    displayName: 'Contact',
    pluralDisplayName: 'Contacts',
    color: 'blue',
    bannerGradient: 'linear-gradient(135deg, #E0C3FC 0%, #8EC5FC 100%)', // Purple to Blue
    getDisplayName,
    timelineConfig: {
      includeAssociatedEvents: true,
      associatedEventTypes: [
        'ahoy_email_sent',
        'ahoy_email_received',
        'ahoy_deal_*', // All deal events
      ],
      groupByDate: true,
      defaultFilters: {
        includeAssociated: true,
      },
    },
    routes: {
      list: '/:orgId/crm/contacts',
      detail: '/:orgId/crm/ahoy_contact/:id',
    },
    fullPageRoute: '/:orgId/crm/contacts/:recordId',
    getSubtitle: (object: CrmObject) =>
      getPropertyValue<string>(object.properties?.ahoy_job_title) || null,
    headerActions: [gmailAction, linkedinAction],
    aiSummarySource: 'research',
    detailFieldOrder: [
      { type: 'property', key: 'ahoy_first_name' },
      { type: 'property', key: 'ahoy_last_name' },
      { type: 'property', key: 'ahoy_email' },
      { type: 'property', key: 'ahoy_phone_number' },
      { type: 'property', key: 'ahoy_job_title' },
      { type: 'association', key: 'ahoy_contact_to_company' },
      { type: 'association', key: 'ahoy_contact_to_lifecycle_stage' },
      { type: 'property', key: 'ahoy_linkedin_url' },
      { type: 'association', key: 'ahoy_contact_to_deal' },
      { type: 'association', key: 'ahoy_contact_to_owner' },
      { type: 'property', key: 'ahoy_first_web_source' },
      { type: 'property', key: 'ahoy_last_web_source' },
      { type: 'timestamp', key: 'ahoy_created_at' },
      { type: 'timestamp', key: 'ahoy_updated_at' },
    ],
    createConfig: {
      createProperties: [
        'ahoy_first_name',
        'ahoy_last_name',
        'ahoy_job_title',
        'ahoy_email',
        'ahoy_linkedin_url',
        'ahoy_phone_number',
      ],
      createAssociations: [
        'ahoy_contact_to_lifecycle_stage',
        'ahoy_contact_to_company',
        'ahoy_contact_to_deal',
        'ahoy_contact_to_owner',
      ],
      parseSearchText: (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) {
          return {};
        }
        if (isEmail(trimmed)) {
          return { ahoy_email: trimmed ? [trimmed] : undefined };
        }
        const parsed = parseFullName(trimmed);
        return {
          ahoy_first_name: parsed.first || '',
          ahoy_last_name: parsed.last || '',
        };
      },
    },
    highlights: [
      // Six contact highlights in a 3×2 grid:
      //   Company      |  Phone        |  Last emailed
      //   Last met     |  Next meeting |  Touchpoints (30d)
      //
      // Email and Job title intentionally omitted as highlight cards —
      // Email is already shown in the left-column property list and is
      // the click target for the header strip's Draft email affordance;
      // Job title is shown in the property list and is duplicated in
      // the heading subtitle. Phone has no equivalent affordance
      // elsewhere on the page so it stays as a card. Lifecycle stage is
      // shown in the left-column property list (as a stage badge), so it
      // doesn't need to also occupy a metric card.
      {
        kind: 'association',
        label: 'Company',
        associationType: 'ahoy_contact_to_company',
      },
      { kind: 'property', label: 'Phone', propertyName: 'ahoy_phone_number' },
      {
        kind: 'last_event',
        label: 'Last Emailed',
        eventTypeNames: ['ahoy_email_sent'],
      },
      { kind: 'computed', label: 'Last Met', compute: 'contact_last_met' },
      {
        kind: 'computed',
        label: 'Next Meeting',
        compute: 'next_interaction',
      },
      {
        // Composite touchpoints metric — sums emails + meetings
        // (calls aren't tracked as a first-class entity type yet, so
        // they're omitted; when call entities/events are added the
        // sub-card just needs a third query). The previous
        // `entity_count` kind only counted emails and read as
        // misleading when the contact had a recent meeting.
        kind: 'computed',
        label: 'Touchpoints (30d)',
        compute: 'contact_touchpoints_30d',
      },
    ],
    tabs: ['overview', 'activity', 'emails', 'meetings', 'notes', 'files'],
    // Contacts have no separate "summary" card — the overview's identity
    // section carries the data a summary would show. SummarySection
    // distinguishes `null` (suppress) from `undefined` (fall through).
    components: {
      summary: null,
      subHeader: ContactSubHeader,
      typeSpecificSections: CONTACT_TYPE_SPECIFIC_SECTIONS,
      hoverBody: EntityHoverContactPreview,
    },
  },

  ahoy_company: {
    displayName: 'Company',
    pluralDisplayName: 'Companies',
    color: 'green',
    bannerGradient: 'linear-gradient(135deg, #10B981 0%, #3B82F6 100%)', // Green to Blue
    getDisplayName,
    timelineConfig: {
      includeAssociatedEvents: true,
      associatedEventTypes: [
        'ahoy_contact_*', // All contact events
        'ahoy_deal_*', // All deal events
      ],
      groupByDate: true,
      defaultFilters: {
        includeAssociated: true,
      },
    },
    routes: {
      list: '/:orgId/crm/companies',
      detail: '/:orgId/crm/ahoy_company/:id',
    },
    fullPageRoute: '/:orgId/crm/companies/:recordId',
    getSubtitle: (object: CrmObject) =>
      getPropertyValue<string>(object.properties?.ahoy_domain) || null,
    headerActions: [websiteAction],
    aiSummarySource: 'research',
    hiddenProperties: [
      'ahoy_website', // Redundant with domain
      'ahoy_linkedin_id', // Internal enrichment ID
      'ahoy_country_code', // Part of phone number, not standalone
      'ahoy_company_type', // Not needed in overview
      'ahoy_postal_code', // Not needed in overview
      'ahoy_address', // Replaced by city/region/country fields
    ],
    detailFieldOrder: [
      { type: 'property', key: 'ahoy_name' },
      { type: 'property', key: 'ahoy_domain' },
      { type: 'property', key: 'ahoy_industry' },
      { type: 'property', key: 'ahoy_description' },
      { type: 'property', key: 'ahoy_headcount_range' },
      { type: 'property', key: 'ahoy_year_founded' },
      // Location fields
      { type: 'property', key: 'ahoy_city' },
      { type: 'property', key: 'ahoy_region' },
      { type: 'property', key: 'ahoy_country' },
      { type: 'property', key: 'ahoy_linkedin_url' },
      { type: 'association', key: 'ahoy_company_to_contact' },
      { type: 'association', key: 'ahoy_company_to_deal' },
      { type: 'association', key: 'ahoy_company_to_owner' },
      { type: 'timestamp', key: 'ahoy_created_at' },
      { type: 'timestamp', key: 'ahoy_updated_at' },
    ],
    createConfig: {
      createProperties: ['ahoy_name', 'ahoy_domain'],
      createAssociations: [
        'ahoy_company_to_contact',
        'ahoy_company_to_deal',
        'ahoy_company_to_owner',
      ],
      parseSearchText: (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) {
          return {};
        }
        const domain = extractDomain(trimmed);
        if (domain) {
          return { ahoy_domain: domain };
        }
        return { ahoy_name: trimmed };
      },
    },
    highlights: [
      {
        kind: 'association_count',
        label: 'Contacts',
        associationType: 'ahoy_contact_to_company',
        // The Contacts count is a shortcut into the Contacts tab.
        linkToTab: 'contacts',
      },
      {
        kind: 'association_count',
        label: 'Deals',
        associationType: 'ahoy_deal_to_company',
      },
      {
        kind: 'computed',
        label: 'Total Deal Value',
        compute: 'company_open_deal_value',
      },
      // "Last activity" tracks genuine interactions (emails, LinkedIn, web
      // form) — NOT bookkeeping / imports / enrichment — so the tile reflects
      // a real touchpoint instead of "just now" after a sync. Mirrors the
      // interaction set used by the Last-interaction line.
      {
        kind: 'last_event',
        label: 'Last Activity',
        eventTypeNames: [
          'ahoy_email_sent',
          'ahoy_email_received',
          'ahoy_linkedin_message_sent',
          'ahoy_linkedin_message_received',
          'ahoy_web_form_submitted',
        ],
      },
      {
        kind: 'computed',
        label: 'Next Meeting',
        compute: 'next_interaction',
      },
      { kind: 'computed', label: 'Location', compute: 'company_location' },
    ],
    tabs: [
      'overview',
      'activity',
      'contacts',
      'emails',
      'meetings',
      'notes',
      'files',
    ],
    // No separate summary card — see ahoy_contact for the same rationale.
    components: {
      summary: null,
      subHeader: CompanySubHeader,
      typeSpecificSections: [
        { kind: 'section', config: RISKS_SECTION },
        {
          kind: 'custom',
          key: 'company-people',
          component: CompanyPeopleSection,
        },
      ],
      hoverBody: EntityHoverCompanyPreview,
    },
  },

  ahoy_deal: {
    displayName: 'Deal',
    pluralDisplayName: 'Deals',
    color: 'amber',
    bannerGradient: 'linear-gradient(135deg, #F59E0B 0%, #EC4899 100%)', // Amber to Pink
    getDisplayName,
    timelineConfig: {
      includeAssociatedEvents: true,
      associatedEventTypes: [
        'ahoy_contact_*', // All contact events
        'ahoy_email_sent',
        'ahoy_email_received',
      ],
      groupByDate: true,
      defaultFilters: {
        includeAssociated: true,
      },
    },
    routes: {
      list: '/:orgId/crm/deals',
      detail: '/:orgId/crm/ahoy_deal/:id',
    },
    fullPageRoute: '/:orgId/crm/deals/:recordId',
    getSubtitle: (object: CrmObject) => {
      const amount = getPropertyValue<DealQuantityValue>(
        object.properties?.ahoy_amount
      );
      if (
        !amount ||
        typeof amount !== 'object' ||
        amount.amount === null ||
        amount.amount === undefined
      ) {
        return null;
      }
      let num: number;
      if (typeof amount.amount === 'string') {
        const trimmed = amount.amount.trim();
        num = trimmed === '' ? NaN : Number(trimmed);
      } else {
        num = amount.amount;
      }
      if (typeof num !== 'number' || !Number.isFinite(num)) {
        return null;
      }
      return amount.unit
        ? formatCurrency(num, amount.unit)
        : `$${num.toLocaleString()}`;
    },
    emptyPlaceholders: {
      ahoy_close_date: 'No close date set',
    },
    detailFieldOrder: [
      { type: 'property', key: 'ahoy_name' },
      { type: 'property', key: 'ahoy_amount' },
      { type: 'association', key: 'ahoy_deal_to_deal_pipeline_stage' },
      { type: 'property', key: 'ahoy_close_date' },
      { type: 'association', key: 'ahoy_deal_to_contact' },
      { type: 'association', key: 'ahoy_deal_to_company' },
      { type: 'association', key: 'ahoy_deal_to_owner' },
      { type: 'timestamp', key: 'ahoy_created_at' },
      { type: 'timestamp', key: 'ahoy_updated_at' },
    ],
    createConfig: {
      createProperties: ['ahoy_name', 'ahoy_amount', 'ahoy_close_date'],
      createAssociations: [
        'ahoy_deal_to_deal_pipeline_stage',
        'ahoy_deal_to_contact',
        'ahoy_deal_to_company',
        'ahoy_deal_to_owner',
      ],
      parseSearchText: (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) {
          return {};
        }
        return { ahoy_name: trimmed };
      },
    },
    highlights: [
      {
        kind: 'association',
        label: 'Stage',
        associationType: 'ahoy_deal_to_deal_pipeline_stage',
      },
      { kind: 'property', label: 'Amount', propertyName: 'ahoy_amount' },
      {
        kind: 'property',
        label: 'Close Date',
        propertyName: 'ahoy_close_date',
      },
      {
        kind: 'association',
        label: 'Owner',
        associationType: 'ahoy_deal_to_owner',
      },
      {
        kind: 'association',
        label: 'Primary Contact',
        associationType: 'ahoy_deal_to_contact',
      },
      {
        kind: 'association',
        label: 'Company',
        associationType: 'ahoy_deal_to_company',
      },
    ],
    tabs: [
      'overview',
      'activity',
      'contacts',
      'emails',
      'meetings',
      'notes',
      'files',
    ],
    // No separate summary card — see ahoy_contact for the same rationale.
    components: {
      summary: null,
      subHeader: DealSubHeader,
      // People kept as a bespoke section (unchanged); meetings + notes are
      // declarative, resolved off the deal id via the materialized
      // ahoy_deal_to_meeting / ahoy_case_to_meeting_notes associations.
      typeSpecificSections: [
        { kind: 'section', config: RISKS_SECTION },
        { kind: 'custom', key: 'deal-people', component: DealPeopleSection },
        { kind: 'section', config: UPCOMING_MEETINGS_SECTION },
        { kind: 'section', config: MEETING_NOTES_SECTION },
      ],
    },
  },

  ahoy_meeting: {
    displayName: 'Meeting',
    pluralDisplayName: 'Meetings',
    color: 'indigo',
    bannerGradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', // Indigo to Purple
    getDisplayName,
    readOnly: true,
    showAvatar: false,
    headerVariant: 'compact',
    timelineDefaultExpanded: false,
    timelineConfig: {
      includeAssociatedEvents: false,
      associatedEventTypes: [],
      groupByDate: true,
      defaultFilters: {},
    },
    routes: {
      list: '/:orgId/crm/meetings',
      detail: '/:orgId/crm/ahoy_meeting/:id',
    },
    fullPageRoute: '/:orgId/crm/meetings/:recordId',
    backNavigation: {
      route: '/:orgId/calendar',
      label: 'Back to calendar',
    },
    aiSummarySource: 'meeting_notes',
    hiddenProperties: [
      'ahoy_title', // Shown as display name in header
      'ahoy_start_time', // Rendered in MeetingSubHeader
      'ahoy_end_time', // Rendered in MeetingSubHeader
      'ahoy_meeting_link', // Rendered in SummarySection
      'ahoy_invitations', // Rendered as attendees in SummarySection
      'ahoy_description', // Not displayed in slideout
      'ahoy_calendar_link', // Internal
      'ahoy_timezone', // Used for formatting, not displayed directly
      'ahoy_status', // Hidden from overview
      'ahoy_is_recurring', // Internal
      'ahoy_rrule', // Internal
      'ahoy_color', // Internal
      'ahoy_display_name', // Shown as display name in header
      'ahoy_hidden', // Internal
      'ahoy_updated_at', // Hidden from overview
    ],
    detailFieldOrder: [
      { type: 'property', key: 'ahoy_location' },
      { type: 'association', key: 'ahoy_meeting_to_company' },
      { type: 'association', key: 'ahoy_meeting_to_contact' },
      { type: 'timestamp', key: 'ahoy_created_at' },
    ],
    highlights: [
      { kind: 'property', label: 'When', propertyName: 'ahoy_start_time' },
      { kind: 'computed', label: 'Duration', compute: 'meeting_duration' },
      {
        kind: 'association_count',
        label: 'Attendees',
        associationType: 'ahoy_contact_to_meeting',
      },
      { kind: 'property', label: 'Location', propertyName: 'ahoy_location' },
      { kind: 'computed', label: 'Prep', compute: 'meeting_prep_status' },
      { kind: 'computed', label: 'Summary', compute: 'meeting_summary_status' },
    ],
    tabs: ['overview', 'activity', 'prep', 'notes', 'files', 'associations'],
    // ahoy_booking_answers is a `json` snapshot copied from the originating booking.
    // Decompose it into native "Booking questions" rows (same as the booking
    // record's ahoy_custom_answers) instead of the raw JSON PropertyValueLabel
    // would show.
    customPropertyReadRenderers: {
      ahoy_booking_answers: BookingAnswersRows,
    },
    // Meeting summary composes Attendees (with store-resolved contact upgrade)
    // and an optional meeting link — see meeting-summary.tsx.
    components: {
      summary: MeetingSummary,
      subHeader: MeetingSubHeader,
      typeSpecific: MeetingTypeSpecific,
      // Recurring-series list sits directly above the activity timeline.
      beforeTimeline: MeetingSeriesSection,
    },
  },

  ahoy_note: {
    // Notes are first-class CRM records (`/crm/ahoy-note/:recordId`). This
    // entry exists primarily to register the per-entity-type component
    // overrides — the slideout body is rendered by `NoteRecordOverview`
    // (which delegates to the same NoteCard used by the notes tab in other
    // slideouts), and the standard "record summary" card is suppressed via
    // `summary: null` because the overview already carries the title +
    // content.
    displayName: 'Note',
    pluralDisplayName: 'Notes',
    color: 'yellow',
    bannerGradient: 'linear-gradient(135deg, #FEF3C7 0%, #FBBF24 100%)', // Sticky-note yellow
    getDisplayName,
    // The slideout's RecordHeader edit affordance toggles OverviewSection's
    // edit mode — which we don't render for notes (NoteCard owns its own
    // pencil affordance). Hiding it here avoids a dead button.
    readOnly: true,
    // …but unlike other readOnly types (meetings, research) we DO want
    // the standard kebab → Delete dropdown in the slideout header. The
    // NoteCard's inline trash button is being removed in favour of the
    // header dropdown so notes match every other deletable record.
    showRecordActionMenu: true,
    // Surface an "Edit note" button in the slideout header. NoteRecordOverview
    // edits the note in place (title + body become editable where they sit),
    // so the trigger belongs in the header rather than as an inline pencil on
    // the note body. RecordHeader wires the click to the override's edit mode
    // through RecordViewContext.
    overviewHeaderEditLabel: 'Edit note',
    // Notes don't have a meaningful avatar (no email / no person identity).
    showAvatar: false,
    // The note IS the record — its body + linked records ARE the content, so
    // the generic reverse-chronological activity timeline is redundant noise.
    // Suppressed via the same flag ahoy_email_thread uses (see RecordHeader's
    // showTimeline computation, which checks this entity config flag).
    hideTimelineSection: true,
    // Routes use snake_case entity_type_name (matches other registry entries
    // and the registry's invariant test). The list page mounts under the
    // `crm/:entitySlug` catch-all in dashboard.routes.tsx, which normalizes
    // kebab → snake via toSnakeCase, so the SideNavigation's `/crm/ahoy-note`
    // and a programmatic `/crm/ahoy_note` resolve to the same place.
    routes: {
      list: '/:orgId/crm/ahoy_note',
      detail: '/:orgId/crm/ahoy_note/:id',
    },
    components: {
      summary: null,
      overview: NoteRecordOverview,
    },
  },

  ahoy_task: {
    displayName: 'Task',
    pluralDisplayName: 'Tasks',
    color: 'teal',
    bannerGradient: 'linear-gradient(135deg, #CCFBF1 0%, #14B8A6 100%)', // Teal
    getDisplayName,
    // Tasks edit per-field inside TaskRecordOverview (no generic OverviewSection),
    // so suppress the generic edit toggle. The title is renamed in the record
    // header (click-to-edit, via the display name's `ahoy_title` source), so
    // there is no header "Edit task" action.
    readOnly: true,
    showRecordActionMenu: true,
    showAvatar: false,
    // TaskRecordOverview + TaskTabsSection present the record; generic timeline redundant
    // (matches how tasks rendered under ahoy_note).
    hideTimelineSection: true,
    // useFileAssociation resolves ahoy_task's own dedicated ahoy_file_to_task/
    // ahoy_task_to_file association type (ahoy_task is excluded from the
    // deal-shaped case-kind ahoy_file_to_deal/ahoy_deal_to_file rules), so the
    // generic FilesSection would otherwise render alongside TaskAttachments.
    // hideFilesSection is set explicitly to suppress the generic
    // FilesSection/FilesTabContent UI here, since the task surface renders
    // TaskAttachments instead (same pattern as ahoy_note above).
    hideFilesSection: true,
    routes: {
      list: '/:orgId/crm/ahoy_task',
      detail: '/:orgId/crm/ahoy_task/:id',
    },
    components: {
      summary: null,
      overview: TaskRecordOverview,
    },
  },

  ahoy_meeting_notes: {
    displayName: 'Meeting Summary',
    pluralDisplayName: 'Meeting Summaries',
    color: 'purple',
    bannerGradient: 'linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)', // Purple to Fuchsia
    getDisplayName,
    headerVariant: 'compact',
    readOnly: true,
    showAvatar: false,
    timelineDefaultExpanded: false,
    detailFieldOrder: [
      { type: 'property', key: 'ahoy_notes_source' },
      { type: 'property', key: 'ahoy_recording_url' },
      { type: 'timestamp', key: 'ahoy_created_at' },
    ],
    hiddenProperties: [
      'ahoy_title',
      'ahoy_summary',
      'ahoy_action_items',
      'ahoy_key_topics',
      'ahoy_speaker_map',
      'ahoy_transcript',
      'ahoy_raw_transcript',
    ],
    routes: {
      list: '/:orgId/crm/meeting-notes',
      detail: '/:orgId/crm/ahoy_meeting_notes/:id',
    },
    fullPageRoute: '/:orgId/meeting-summaries/:recordId',
    // Meeting-notes' summary card surfaces the AI-generated meeting summary,
    // key topics, and action items — see meeting-notes-detail-section.tsx.
    components: {
      summary: MeetingNotesDetailSection,
      subHeader: AccessBadgeSubHeader,
    },
  },
};
